import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

import { fetchArticlesForDate, fetchQuoteForDate } from "@/lib/queries";
import { TOPIC_LABEL, type Topic } from "@/lib/topics";
import { PaywallBadge } from "@/components/PaywallBadge";
import { detectFormat, formatLabel } from "@/lib/format-detector";

import { SafeExternalLink } from "@/components/SafeExternalLink";
import { WikiName } from "@/lib/wiki-link";
import { useReadArticles } from "@/hooks/use-read-articles";
import { usePaywallFilter } from "@/hooks/use-paywall-filter";
import { normalizeTitle } from "@/lib/normalize-title";

const SITE_URL = "https://flaneriet.online";
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

function isBookish(url: string, title: string) {
  const f = detectFormat(url, title);
  return f === "book" || f === "audiobook";
}

function prettyDate(date: string) {
  const d = new Date(`${date}T12:00:00+01:00`);
  return d.toLocaleDateString("da-DK", { day: "numeric", month: "long", year: "numeric" });
}

export const Route = createFileRoute("/dagbog/$date")({
  loader: async ({ params, context }) => {
    if (!DATE_RE.test(params.date)) throw notFound();
    const [articles, quote] = await Promise.all([
      context.queryClient.ensureQueryData({
        queryKey: ["dagbog-articles", params.date],
        queryFn: () => fetchArticlesForDate(params.date),
      }),
      context.queryClient.ensureQueryData({
        queryKey: ["dagbog-quote", params.date],
        queryFn: () => fetchQuoteForDate(params.date),
      }),
    ]);
    return { articles, quote };
  },
  head: ({ params, loaderData }) => {
    const url = `${SITE_URL}/dagbog/${params.date}`;
    const pretty = DATE_RE.test(params.date) ? prettyDate(params.date) : params.date;
    const q = loaderData?.quote as { text?: string; author?: string | null } | null | undefined;
    const title = `Flâneriet, ${pretty} – dagens ord og friske strejf`;
    const description = q?.text
      ? `“${q.text.replace(/\s+/g, " ").trim().slice(0, 140)}”${q.author ? ` – ${q.author}` : ""}`
      : `Flâneriets arkiv for ${pretty}: dagens citat og de friske strejf fra vin, kunst, jazz, poesi, litteratur, mad og cykling.`;
    const scripts: Array<{ type: string; children: string }> = [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          headline: title,
          description,
          datePublished: `${params.date}T06:00:00+01:00`,
          url,
          inLanguage: "da-DK",
          mainEntityOfPage: url,
          publisher: { "@type": "Organization", name: "Flâneriet", url: SITE_URL },
        }),
      },
    ];
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "article" },
        { property: "og:url", content: url },
        { property: "og:image", content: `${SITE_URL}/og-image.jpg` },
        { name: "twitter:image", content: `${SITE_URL}/og-image.jpg` },
        { property: "article:published_time", content: `${params.date}T06:00:00+01:00` },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts,
    };
  },
  component: DagbogView,
  notFoundComponent: () => (
    <div className="mx-auto max-w-2xl px-6 py-24 text-center">
      <p className="eyebrow">404</p>
      <h1 className="mt-3 font-serif text-3xl">Denne dag findes ikke i dagbogen</h1>
      <p className="mt-3 meta">Datoen skal have formatet ÅÅÅÅ-MM-DD.</p>
      <Link to="/" className="mt-6 inline-block border-b border-bordeaux pb-0.5 text-bordeaux">Til forsiden</Link>
    </div>
  ),
  errorComponent: () => (
    <div className="mx-auto max-w-2xl px-6 py-24 text-center">
      <h1 className="font-serif text-2xl">Noget faldt til ro på en forkert måde</h1>
      <p className="mt-3 meta">Prøv igen om et øjeblik.</p>
    </div>
  ),
});

function DagbogView() {
  const { date } = Route.useParams();
  const { articles = [], quote = null } = Route.useLoaderData() ?? {};
  const { markRead, isRead } = useReadArticles();
  const { hidePaywall } = usePaywallFilter();

  const articlesQuery = useQuery({
    queryKey: ["dagbog-articles", date],
    queryFn: () => fetchArticlesForDate(date),
    initialData: articles,
  });
  const quoteQuery = useQuery({
    queryKey: ["dagbog-quote", date],
    queryFn: () => fetchQuoteForDate(date),
    initialData: quote,
  });

  const list = useMemo(
    () =>
      (articlesQuery.data ?? [])
        .filter((a: any) => !isBookish(a.original_url, a.title))
        .filter((a: any) => !hidePaywall || !a.paywall),
    [articlesQuery.data, hidePaywall]
  );

  const q = quoteQuery.data;
  const pretty = prettyDate(date);

  return (
    <div>
      <section className="pt-12 pb-6 text-center">
        <p className="eyebrow">Fra dagbogen</p>
        <h1 className="mt-3 font-serif text-4xl md:text-5xl tracking-tight text-ink">{pretty}</h1>
        <p className="mt-3 meta italic">
          Sådan så Flâneriet ud den dag – citat og friske strejf, som de blev fundet.
        </p>
        <p className="mt-4">
          <Link to="/" className="eyebrow text-bordeaux border-b border-bordeaux pb-0.5">
            ← Til forsiden
          </Link>
        </p>
      </section>

      <section className="py-14 md:py-20 text-center border-t border-b border-rule">
        <p className="eyebrow">Dagens ord</p>
        {q ? (
          <>
            <blockquote className="mx-auto mt-6 w-[80vw] max-w-5xl font-serif text-[1.5rem] md:text-[1.85rem] leading-[1.45] italic text-ink">
              {"“"}{q.text}{"”"}
            </blockquote>
            {q.author && <p className="mt-5 meta">– <WikiName name={q.author} /></p>}
          </>
        ) : (
          <p className="mt-6 meta italic">Ingen citater fundet for denne dag.</p>
        )}
      </section>

      <section className="py-10">
        <div className="flex items-baseline justify-between border-b-2 border-ink pb-3.5">
          <h2 className="font-serif text-[1.9rem] md:text-[2.3rem] leading-[1.1] m-0 text-ink">Friske strejf</h2>
          <p className="eyebrow">{pretty}</p>
        </div>
        {list.length === 0 ? (
          <p className="mt-6 meta italic">Ingen strejf blev fundet den dag.</p>
        ) : (
          <ul className="mt-6 divide-y divide-rule">
            {list.map((a: any) => (
              <li key={a.id} className="py-4">
                <SafeExternalLink
                  href={a.original_url}
                  onOpen={() => markRead(a.id)}
                  className="group grid gap-y-1 gap-x-4 grid-cols-[minmax(0,1fr)_auto] md:grid-cols-[7rem_minmax(0,1fr)_auto] md:items-baseline"
                >
                  <span className="eyebrow md:w-28 md:shrink-0 md:col-start-1 md:row-start-1">
                    {TOPIC_LABEL[a.topic as Topic]}
                  </span>
                  <span className="justify-self-end shrink-0 md:col-start-3 md:row-start-1">
                    <PaywallBadge paywall={a.paywall} />
                  </span>
                  <span className={`col-span-2 md:col-span-1 md:col-start-2 md:row-start-1 min-w-0 ${!isRead(a.id) ? "pl-4" : ""}`}>
                    <span className="font-serif text-[1.2rem] md:text-[1.25rem] block group-hover:text-bordeaux transition-colors duration-500 text-ink flex items-baseline">
                      {!isRead(a.id) && (
                        <span className="inline-block w-[6px] h-[6px] rounded-full bg-bordeaux flex-shrink-0 translate-y-[-1px] -ml-4 mr-2.5" aria-hidden="true" />
                      )}
                      {normalizeTitle(a.title)}
                    </span>
                    <span className="meta italic block mt-1">
                      {a.source}
                      {(() => {
                        const fmt = detectFormat(a.original_url, a.title);
                        return fmt !== "article" ? <span className="text-bordeaux"> · {formatLabel(fmt)}</span> : null;
                      })()}
                    </span>
                  </span>
                </SafeExternalLink>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
